/**
 * NativeDateInput / NativeTimeInput
 *
 * Drop-in replacements for <input type="date"> and <input type="time">.
 *
 * On Capacitor native Android the OS date/time picker dialogs AND native
 * <select> spinners render poorly inside WebViews.  On native these components
 * render a fully custom React calendar / time picker in a modal overlay so no
 * OS dialog is ever triggered.  On web the standard HTML inputs are used.
 *
 * Interface matches native inputs exactly:
 *   - Date:  value="YYYY-MM-DD"  onChange fires a synthetic ChangeEvent
 *   - Time:  value="HH:MM"       onChange fires a synthetic ChangeEvent (24h)
 */

import { Capacitor } from '@capacitor/core';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { notifyModalOpen, notifyModalClose } from '../../utils/modalState';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';

const IS_NATIVE = Capacitor.isNativePlatform();

type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeSyntheticEvent(value: string): React.ChangeEvent<HTMLInputElement> {
  return { target: { value } } as React.ChangeEvent<HTMLInputElement>;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function parseDate(raw: string | undefined) {
  const now = new Date();
  if (!raw) return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
  const parts = raw.split('-').map(Number);
  return { y: parts[0] || now.getFullYear(), m: parts[1] || 1, d: parts[2] || 1 };
}

function parseTime(raw: string | undefined) {
  if (!raw) return { h: 9, min: 0 };
  const parts = raw.split(':').map(Number);
  return { h: parts[0] ?? 9, min: parts[1] ?? 0 };
}

function formatDisplayDate(y: number, m: number, d: number) {
  return `${MONTH_NAMES[m - 1].slice(0, 3)} ${d}, ${y}`;
}

function formatDisplayTime(h: number, min: number) {
  const isPm = h >= 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, '0')}:${String(min).padStart(2, '0')} ${isPm ? 'PM' : 'AM'}`;
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function firstDayOfMonth(year: number, month: number) {
  return new Date(year, month - 1, 1).getDay();
}

// ---------------------------------------------------------------------------
// Modal overlay wrapper (portal)
// ---------------------------------------------------------------------------

const ModalOverlay: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    notifyModalOpen();
    return () => { document.body.style.overflow = prev; notifyModalClose(); };
  }, []);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-sm bg-white rounded-t-2xl shadow-2xl pb-safe"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// CalendarPicker (used by NativeDateInput on native)
// ---------------------------------------------------------------------------

interface CalendarPickerProps {
  year: number;
  month: number;
  day: number;
  onSelect: (y: number, m: number, d: number) => void;
  onClose: () => void;
}

const CalendarPicker: React.FC<CalendarPickerProps> = ({ year, month, day, onSelect, onClose }) => {
  const [viewY, setViewY] = useState(year);
  const [viewM, setViewM] = useState(month);

  const today = new Date();
  const totalDays = daysInMonth(viewY, viewM);
  const startDay = firstDayOfMonth(viewY, viewM);

  const prevMonth = () => {
    if (viewM === 1) { setViewM(12); setViewY(y => y - 1); }
    else setViewM(m => m - 1);
  };
  const nextMonth = () => {
    if (viewM === 12) { setViewM(1); setViewY(y => y + 1); }
    else setViewM(m => m + 1);
  };

  const cells: (number | null)[] = [
    ...Array(startDay).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];
  // Pad to complete grid rows
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <ModalOverlay onClose={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button type="button" onClick={onClose} className="text-sm text-gray-500 font-medium px-2 py-1">
          Cancel
        </button>
        <span className="text-sm font-semibold text-gray-800">Select Date</span>
        <button
          type="button"
          onClick={() => {
            const t = new Date();
            onSelect(t.getFullYear(), t.getMonth() + 1, t.getDate());
          }}
          className="text-sm text-primary font-medium px-2 py-1"
        >
          Today
        </button>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between px-4 py-3">
        <button type="button" onClick={prevMonth} className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <span className="text-base font-semibold text-gray-900">
          {MONTH_NAMES[viewM - 1]} {viewY}
        </span>
        <button type="button" onClick={nextMonth} className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors">
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 px-2 mb-1">
        {DAY_NAMES.map(n => (
          <div key={n} className="text-center text-xs font-medium text-gray-400 py-1">{n}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 px-2 pb-4">
        {cells.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} />;
          const isSelected = viewY === year && viewM === month && d === day;
          const isToday = viewY === today.getFullYear() && viewM === today.getMonth() + 1 && d === today.getDate();
          return (
            <button
              key={d}
              type="button"
              onClick={() => onSelect(viewY, viewM, d)}
              className={`
                aspect-square flex items-center justify-center rounded-full text-sm font-medium transition-colors mx-0.5 my-0.5
                ${isSelected ? 'bg-primary text-white' : isToday ? 'border border-primary text-primary' : 'text-gray-800 hover:bg-gray-100 active:bg-gray-200'}
              `}
            >
              {d}
            </button>
          );
        })}
      </div>
    </ModalOverlay>
  );
};

// ---------------------------------------------------------------------------
// TimePicker (used by NativeTimeInput on native)
// ---------------------------------------------------------------------------

interface TimePickerProps {
  hour: number;
  minute: number;
  onSelect: (h: number, min: number) => void;
  onClose: () => void;
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

const TimePicker: React.FC<TimePickerProps> = ({ hour, minute, onSelect, onClose }) => {
  const isPm = hour >= 12;
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const [selH, setSelH] = useState(h12);
  const [selMin, setSelMin] = useState(Math.round(minute / 5) * 5 % 60);
  const [selPm, setSelPm] = useState(isPm);

  const confirm = () => {
    let h24 = selH % 12;
    if (selPm) h24 += 12;
    onSelect(h24, selMin);
  };

  const btnBase = 'w-full py-2.5 text-sm font-medium rounded-xl transition-colors text-center';
  const selected = 'bg-primary text-white';
  const unselected = 'text-gray-700 hover:bg-gray-100 active:bg-gray-200';

  return (
    <ModalOverlay onClose={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button type="button" onClick={onClose} className="text-sm text-gray-500 font-medium px-2 py-1">
          Cancel
        </button>
        <span className="text-sm font-semibold text-gray-800">Select Time</span>
        <button type="button" onClick={confirm} className="text-sm text-primary font-medium px-2 py-1">
          Done
        </button>
      </div>

      {/* Preview */}
      <div className="text-center py-2 text-xl font-bold text-gray-900">
        {String(selH).padStart(2, '0')}:{String(selMin).padStart(2, '0')} {selPm ? 'PM' : 'AM'}
      </div>

      {/* Three columns: hours | minutes | AM/PM */}
      <div className="flex gap-2 px-3 pb-4 h-52">
        {/* Hours */}
        <div className="flex-1 overflow-y-auto space-y-1 pr-1" style={{ scrollbarWidth: 'none' }}>
          {HOURS_12.map(h => (
            <button key={h} type="button" onClick={() => setSelH(h)} className={`${btnBase} ${selH === h ? selected : unselected}`}>
              {String(h).padStart(2, '0')}
            </button>
          ))}
        </div>

        {/* Minutes */}
        <div className="flex-1 overflow-y-auto space-y-1 pr-1" style={{ scrollbarWidth: 'none' }}>
          {MINUTES.map(m => (
            <button key={m} type="button" onClick={() => setSelMin(m)} className={`${btnBase} ${selMin === m ? selected : unselected}`}>
              {String(m).padStart(2, '0')}
            </button>
          ))}
        </div>

        {/* AM / PM */}
        <div className="w-16 flex flex-col gap-2 pt-1">
          <button type="button" onClick={() => setSelPm(false)} className={`${btnBase} ${!selPm ? selected : unselected}`}>AM</button>
          <button type="button" onClick={() => setSelPm(true)} className={`${btnBase} ${selPm ? selected : unselected}`}>PM</button>
        </div>
      </div>
    </ModalOverlay>
  );
};

// ---------------------------------------------------------------------------
// NativeDateInput
// ---------------------------------------------------------------------------

export function NativeDateInput({
  value,
  onChange,
  className,
  style,
  id,
  'aria-label': ariaLabel,
  required,
  ...rest
}: InputProps) {
  const [open, setOpen] = useState(false);

  if (!IS_NATIVE) {
    return (
      <input
        type="date"
        value={value}
        onChange={onChange}
        className={className}
        style={style}
        id={id}
        aria-label={ariaLabel}
        required={required}
        {...rest}
      />
    );
  }

  const { y, m, d } = parseDate(value as string | undefined);
  const displayVal = value ? formatDisplayDate(y, m, d) : '';

  const handleSelect = (ny: number, nm: number, nd: number) => {
    setOpen(false);
    if (!onChange) return;
    onChange(makeSyntheticEvent(
      `${ny}-${String(nm).padStart(2, '0')}-${String(nd).padStart(2, '0')}`
    ));
  };

  return (
    <>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel ?? 'Select date'}
        onClick={() => setOpen(true)}
        className={`${className ?? ''} flex items-center gap-2 text-left`}
        style={style}
      >
        <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
        <span className={displayVal ? 'text-gray-900' : 'text-gray-400'}>
          {displayVal || 'Select date…'}
        </span>
      </button>

      {open && (
        <CalendarPicker
          year={y} month={m} day={d}
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// NativeTimeInput
// ---------------------------------------------------------------------------

export function NativeTimeInput({
  value,
  onChange,
  className,
  style,
  id,
  'aria-label': ariaLabel,
  required,
  ...rest
}: InputProps) {
  const [open, setOpen] = useState(false);

  if (!IS_NATIVE) {
    return (
      <input
        type="time"
        value={value}
        onChange={onChange}
        className={className}
        style={style}
        id={id}
        aria-label={ariaLabel}
        required={required}
        {...rest}
      />
    );
  }

  const { h, min } = parseTime(value as string | undefined);
  const displayVal = value ? formatDisplayTime(h, min) : '';

  const handleSelect = (nh: number, nmin: number) => {
    setOpen(false);
    if (!onChange) return;
    onChange(makeSyntheticEvent(`${String(nh).padStart(2, '0')}:${String(nmin).padStart(2, '0')}`));
  };

  return (
    <>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel ?? 'Select time'}
        onClick={() => setOpen(true)}
        className={`${className ?? ''} flex items-center gap-2 text-left`}
        style={style}
      >
        <Clock className="w-4 h-4 text-gray-400 shrink-0" />
        <span className={displayVal ? 'text-gray-900' : 'text-gray-400'}>
          {displayVal || 'Select time…'}
        </span>
      </button>

      {open && (
        <TimePicker
          hour={h} minute={min}
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
